import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";

// Firebase Auth
import { 
  loginWithEmail, 
  loginWithGoogle, 
  logout as firebaseLogout, 
  onAuthChange, 
  getIdToken,
  getTokenClaims 
} from "@/lib/firebase";

// Pages
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import VerifyEmailChange from "@/pages/VerifyEmailChange";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Locations from "@/pages/Locations";
import Creatives from "@/pages/Creatives";
import Campaigns from "@/pages/Campaigns";
import CampaignDetail from "@/pages/CampaignDetail";
import Targeting from "@/pages/Targeting";
import Analytics from "@/pages/Analytics";
import Attribution from "@/pages/Attribution";
import Billing from "@/pages/Billing";
import Settings from "@/pages/Settings";
import Profile from "@/pages/Profile";
import ArchivedCampaigns from "@/pages/ArchivedCampaigns";
import Resources from "@/pages/Resources";
import CompanyAssets from "@/pages/CompanyAssets";
import AdminAccounts from "@/pages/AdminAccounts";
import AdminUsers from "@/pages/AdminUsers";
import AdServing from "@/pages/AdServing";
import DrivePanel from "@/pages/DrivePanel";
import DrivePanelDemo from "@/pages/DrivePanelDemo";
import AdminAccountRequests from "@/pages/AdminAccountRequests";
import Team from "@/pages/Team";
import AcceptInvite from "@/pages/AcceptInvite";

// Admin Pages
import AdvertisersList from "@/pages/admin/AdvertisersList";
import AdvertiserContext from "@/pages/admin/AdvertiserContext";
import AdvertiserLocations from "@/pages/admin/AdvertiserLocations";
import AdvertiserCreatives from "@/pages/admin/AdvertiserCreatives";
import AdvertiserCampaigns from "@/pages/admin/AdvertiserCampaigns";
import AdvertiserBilling from "@/pages/admin/AdvertiserBilling";
import AdminCategories from "@/pages/admin/Categories";
import TierPricing from "@/pages/admin/TierPricing";
import AIModels from "@/pages/admin/AIModels";

// Components
import Sidebar from "@/components/Sidebar";
import EnvBanner from "@/components/EnvBanner";
import ErrorBoundary from "@/components/ErrorBoundary";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Listen for Firebase auth state changes
    const unsubscribe = onAuthChange(async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Get the ID token
          const idToken = await firebaseUser.getIdToken();
          setToken(idToken);
          
          // Get custom claims (role, account_id)
          const claims = await getTokenClaims();
          
          // Sync with backend to get full user profile
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${idToken}` }
          });
          
          setUser({
            ...response.data,
            firebase_uid: firebaseUser.uid,
            email: firebaseUser.email,
            display_name: firebaseUser.displayName,
            role: claims?.role || response.data?.role,
            account_id: claims?.account_id || response.data?.account_id
          });
        } catch (error) {
          console.error("Error syncing user with backend:", error);
          // If backend sync fails, use Firebase claims only
          const claims = await getTokenClaims();
          if (claims?.role) {
            setUser({
              firebase_uid: firebaseUser.uid,
              email: firebaseUser.email,
              display_name: firebaseUser.displayName,
              role: claims.role,
              account_id: claims.account_id
            });
          } else {
            // No role means user isn't properly set up
            setUser(null);
            setToken(null);
          }
        }
      } else {
        setUser(null);
        setToken(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Refresh token periodically (Firebase tokens expire after 1 hour)
  useEffect(() => {
    if (!user) return;
    
    const refreshToken = async () => {
      try {
        const newToken = await getIdToken(true); // Force refresh
        if (newToken) {
          setToken(newToken);
        }
      } catch (error) {
        console.error("Token refresh failed:", error);
      }
    };

    // Refresh every 55 minutes
    const interval = setInterval(refreshToken, 55 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  const login = async (email, password) => {
    try {
      const result = await loginWithEmail(email, password);
      
      // Wait for auth state to be fully processed
      // The onAuthChange listener will set the user, but we need to wait for it
      const idToken = await result.user.getIdToken();
      const claims = await getTokenClaims();
      
      // Manually set the state here to avoid race condition
      setToken(idToken);
      
      try {
        // Try to sync with backend
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${idToken}` }
        });
        
        setUser({
          ...response.data,
          firebase_uid: result.user.uid,
          email: result.user.email,
          display_name: result.user.displayName,
          role: claims?.role || response.data?.role,
          account_id: claims?.account_id || response.data?.account_id
        });
      } catch (backendError) {
        // If backend sync fails, use Firebase claims
        if (claims?.role) {
          setUser({
            firebase_uid: result.user.uid,
            email: result.user.email,
            display_name: result.user.displayName,
            role: claims.role,
            account_id: claims.account_id
          });
        } else {
          throw new Error("Account not properly configured. Please contact support.");
        }
      }
      
      return result;
    } catch (error) {
      // Map Firebase error codes to user-friendly messages
      const errorMap = {
        'auth/user-not-found': 'No account found with this email',
        'auth/wrong-password': 'Invalid password',
        'auth/invalid-email': 'Invalid email address',
        'auth/user-disabled': 'This account has been disabled',
        'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
        'auth/invalid-credential': 'Invalid email or password'
      };
      throw new Error(errorMap[error.code] || error.message);
    }
  };

  const loginGoogle = async () => {
    try {
      const result = await loginWithGoogle();
      // After Google sign-in, we need to verify/register the user in our backend
      const idToken = await result.user.getIdToken();
      
      // Sync with backend (will create user if needed)
      await axios.post(`${API}/auth/firebase-sync`, {
        firebase_uid: result.user.uid,
        email: result.user.email,
        display_name: result.user.displayName
      }, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
      
      return result;
    } catch (error) {
      const errorMap = {
        'auth/popup-closed-by-user': 'Sign-in cancelled',
        'auth/popup-blocked': 'Pop-up blocked. Please allow pop-ups for this site.',
        'auth/account-exists-with-different-credential': 'An account already exists with this email'
      };
      throw new Error(errorMap[error.code] || error.message);
    }
  };

  const logout = async () => {
    try {
      await firebaseLogout();
      setUser(null);
      setToken(null);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    loginGoogle,
    logout,
    isAdmin: user?.role === "MASTER_ADMIN" || user?.role === "BOOMA_ADMIN",
    isMasterAdmin: user?.role === "MASTER_ADMIN"
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Layout with Sidebar
const AppLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      <main className={`flex-1 transition-all duration-300 ${sidebarOpen ? "ml-64" : "ml-20"}`}>
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

function AppRoutes() {
  const { user, isAdmin } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/verify-email-change" element={<VerifyEmailChange />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Dashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/locations"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Locations />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/creatives"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Creatives />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/creatives/assets"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CompanyAssets />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Campaigns />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/archived"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ArchivedCampaigns />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CampaignDetail />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/targeting"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Targeting />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Analytics />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/attribution"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Attribution />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Billing />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/resources"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Resources />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Settings />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Profile />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Team />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/accept-invite"
        element={<AcceptInvite />}
      />
      <Route
        path="/admin/accounts"
        element={
          <ProtectedRoute>
            <AppLayout>
              {isAdmin ? <AdminAccounts /> : <Navigate to="/" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/advertisers"
        element={
          <ProtectedRoute>
            <AppLayout>
              {isAdmin ? <AdvertisersList /> : <Navigate to="/" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/advertisers/:advertiserId"
        element={
          <ProtectedRoute>
            <AppLayout>
              {isAdmin ? <AdvertiserContext /> : <Navigate to="/" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      >
        <Route path="locations" element={<AdvertiserLocations />} />
        <Route path="creatives" element={<AdvertiserCreatives />} />
        <Route path="campaigns" element={<AdvertiserCampaigns />} />
        <Route path="billing" element={<AdvertiserBilling />} />
      </Route>
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <AppLayout>
              {isAdmin ? <AdminUsers /> : <Navigate to="/" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/account-requests"
        element={
          <ProtectedRoute>
            <AppLayout>
              {isAdmin ? <AdminAccountRequests /> : <Navigate to="/" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/categories"
        element={
          <ProtectedRoute>
            <AppLayout>
              {isAdmin ? <AdminCategories /> : <Navigate to="/" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tier-pricing"
        element={
          <ProtectedRoute>
            <AppLayout>
              {isAdmin ? <TierPricing /> : <Navigate to="/" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ai-models"
        element={
          <ProtectedRoute>
            <AppLayout>
              {isAdmin ? <AIModels /> : <Navigate to="/" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ad-serving"
        element={
          <ProtectedRoute>
            <AppLayout>
              {isAdmin ? <AdServing /> : <Navigate to="/" replace />}
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/drive-panel" element={<DrivePanel />} />
      <Route path="/drive-panel-demo" element={<DrivePanelDemo />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <EnvBanner />
            <Toaster
              position="bottom-right"
              richColors
              toastOptions={{
                className: "bg-card border border-border text-foreground"
              }}
            />
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export { useTheme };
export default App;
